// Labels and free-text matching for the P&ID document list. Shared because the
// document picker and the drawing workspace's annotation-import panel both list
// documents and must describe and filter them the same way.
import type { AnnotationInfo, PidDocument } from "../types";

/** Grouping key for documents with no resolvable domain. */
export const UNASSIGNED_DOMAIN = '__unassigned__';
export type DiagramTypeFilter = 'pid' | 'pfd' | 'all';

/** A document's `plant` field carries its domain's display name. */
export function domainLabel(name: string | null | undefined): string {
  if (!name || name === UNASSIGNED_DOMAIN) return 'Unassigned';
  return name;
}

export function diagramTypeLabel(type: DiagramTypeFilter | null | undefined): string {
  if (type === 'pfd') return 'PFD';
  if (type === 'pid') return 'P&ID';
  return 'All diagrams';
}

/** Grouping key: the document's domain id, or the unassigned sentinel. */
export function getDocumentDomainKey(doc: PidDocument): string {
  return doc.domain_id || UNASSIGNED_DOMAIN;
}

export function getDocumentDiagramType(doc: PidDocument): 'pid' | 'pfd' {
  return doc.diagram_type === 'pfd' ? 'pfd' : 'pid';
}

export function getDocumentPrimaryLabel(doc: PidDocument): string {
  const pnidNo = doc.pnid_no?.trim();
  return pnidNo || doc.original_filename || doc.filename || doc.id;
}

export function getDocumentSecondaryLabel(doc: PidDocument): string {
  const pnidNo = doc.pnid_no?.trim();
  if (pnidNo) {
    return doc.id;
  }
  return doc.original_filename && doc.original_filename !== getDocumentPrimaryLabel(doc)
    ? doc.original_filename
    : doc.id;
}

function normalizeSearchText(value: unknown): string {
  return String(value ?? "")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "");
}

function fieldMatchesSearch(value: unknown, query: string, normalizedQuery: string): boolean {
  const text = String(value ?? "").trim().toLowerCase();
  if (!text) return false;
  if (text.includes(query)) return true;
  return normalizedQuery.length > 0 && normalizeSearchText(text).includes(normalizedQuery);
}

export function documentMatchesSearch(
  doc: PidDocument,
  annotation: AnnotationInfo | null,
  query: string,
): boolean {
  const trimmedQuery = query.trim().toLowerCase();
  if (!trimmedQuery) return true;

  const normalizedQuery = normalizeSearchText(trimmedQuery);
  return [
    doc.pnid_no,
    doc.pnid_title,
    doc.original_filename,
    doc.filename,
    doc.id,
    annotation?.file_id,
    doc.plant,
    domainLabel(doc.plant),
    doc.diagram_type,
    diagramTypeLabel(doc.diagram_type),
    annotation?.status,
    annotation?.user,
  ].some((field) => fieldMatchesSearch(field, trimmedQuery, normalizedQuery));
}
